import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Image as ImageIcon, ExternalLink } from 'lucide-react';
import { api } from '../../api/client';
import Skeleton from './Skeleton';

/**
 * Inline preview of a PDF page from Google Drive with prev/next navigation.
 * Used in COD Checks queue (expanded row) and Document Review queue.
 *
 * Props:
 *   fileId — Drive file ID
 *   initialPage — 0-based page index to start on
 *   maxPages — optional upper bound to disable next button (auto-discovers if not set)
 */
export default function DrivePagePreview({ fileId, initialPage = 0, label = 'Source page' }) {
  const [page, setPage] = useState(initialPage);
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [maxKnownPage, setMaxKnownPage] = useState(initialPage);

  useEffect(() => {
    setPage(initialPage);
  }, [initialPage]);

  useEffect(() => {
    if (!fileId || page == null || page < 0) {
      setLoading(false);
      setErr('No source page available');
      return;
    }
    let cancelled = false;
    let revokeUrl;
    setLoading(true);
    setErr(null);
    api.fetchDrivePreviewBlob(fileId, page)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        if (revokeUrl) URL.revokeObjectURL(revokeUrl);
        revokeUrl = u;
        setUrl(u);
        setLoading(false);
        setMaxKnownPage((prev) => Math.max(prev, page));
      })
      .catch((e) => {
        if (!cancelled) {
          setErr(e.message);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [fileId, page]);

  return (
    <div className="bg-[var(--color-cream-100)] rounded-[var(--radius-md)] border border-[var(--color-warm-100)] overflow-hidden">
      <div className="px-3 py-1.5 border-b border-[var(--color-warm-100)] flex items-center justify-between bg-[var(--color-cream-50)]">
        <div className="flex items-center gap-2">
          <ImageIcon size={12} className="text-[var(--color-warm-500)]" />
          <span className="text-[11px] font-semibold text-[var(--color-warm-600)]">{label}</span>
          <span className="text-[10px] text-[var(--color-warm-500)] ml-1">page {page + 1}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
            className="w-6 h-6 rounded flex items-center justify-center text-[var(--color-warm-600)] hover:bg-[var(--color-cream-200)] disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Previous page"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            onClick={() => setPage(page + 1)}
            className="w-6 h-6 rounded flex items-center justify-center text-[var(--color-warm-600)] hover:bg-[var(--color-cream-200)]"
            aria-label="Next page"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
      <div className="aspect-[8.5/11] bg-white flex items-center justify-center relative">
        {loading && (
          <div className="w-full h-full flex items-center justify-center">
            <Skeleton className="w-full h-full" />
          </div>
        )}
        {err && (
          <div className="text-[12px] text-[var(--color-warm-500)] text-center p-6">
            {err === 'Preview failed' ? `No page ${page + 1} (end of document?)` : err}
            {page > 0 && (
              <button
                onClick={() => setPage(page - 1)}
                className="block mx-auto mt-2 underline text-[var(--color-warm-700)]"
              >
                ← Back to page {page}
              </button>
            )}
          </div>
        )}
        {url && !err && (
          <img src={url} alt={`Source page ${page + 1}`} className="w-full h-full object-contain" />
        )}
      </div>
    </div>
  );
}
